import { useMemo } from "react";
import { FilterBar, type FilterSpec } from "@/components/data";
import { MultiCombobox, type MultiComboboxOption } from "@/components/cost_index/MultiCombobox";
import type { CostIndexSku } from "../api";

export type ScopeSelection = { tracking: string[]; variant: string[]; sku: string[]; region: string | null; channel: string[] };

const choice = (name: string, label: string, values: string[]): FilterSpec => ({ name, label, kind: "choice", anyLabel: "All", options: values.map((value) => ({ value, label: value })) });
const REGIONS = ["UK", "EU", "MENA"];
const text = (value: unknown) => typeof value === "string" && value ? value : null;
const matches = (selected: string[], value: string | null) => selected.length === 0 || value != null && selected.includes(value);
const unique = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b));

export function scopePickerOptions(rows: CostIndexSku[], selection: ScopeSelection): {
  products: MultiComboboxOption[];
  variants: MultiComboboxOption[];
  channels: MultiComboboxOption[];
  skus: MultiComboboxOption[];
} {
  const products = unique(rows.map((row) => row.tracking)).map((value) => ({ value, label: value }));
  const variantRows = rows.filter((row) => matches(selection.tracking, row.tracking));
  const variants = unique(variantRows.map((row) => row.variant)).map((value) => ({
    value,
    label: value,
    hint: unique(variantRows.filter((row) => row.variant === value).map((row) => row.tracking)).join(", "),
  }));
  const channels = unique(rows.map((row) => row.channel).filter((value): value is string => Boolean(value))).map((value) => ({ value, label: value }));
  const skuRows = rows.filter((row) =>
    matches(selection.tracking, row.tracking)
    && matches(selection.variant, row.variant)
    && (!selection.region || row.region === selection.region)
    && matches(selection.channel, row.channel));
  const skus = [...new Map(skuRows.map((row) => [row.sku, {
    value: row.sku,
    label: row.sku,
    hint: [row.tracking, row.variant, row.region, row.channel].filter(Boolean).join(" · "),
  }])).values()].sort((a, b) => a.label.localeCompare(b.label));
  return { products, variants, channels, skus };
}

export function pruneScopeSelection(rows: CostIndexSku[], selection: ScopeSelection): ScopeSelection {
  const options = scopePickerOptions(rows, selection);
  const validVariants = new Set(options.variants.map((option) => option.value));
  const variant = selection.variant.filter((value) => validVariants.has(value));
  const withValidVariants = { ...selection, variant };
  const validSkus = new Set(scopePickerOptions(rows, withValidVariants).skus.map((option) => option.value));
  return { ...withValidVariants, sku: selection.sku.filter((value) => validSkus.has(value)) };
}

export const selectScopeSkus = (selection: ScopeSelection, sku: string[]): ScopeSelection => ({ ...selection, sku });

/** Product, Variant, Region and Channel narrow the searchable SKU set without a SKU changing that scope. */
export function ScopePicker({ rows, selection, onChange, disabled = false }: {
  rows: CostIndexSku[];
  selection: ScopeSelection;
  onChange: (selection: ScopeSelection) => void;
  disabled?: boolean;
}) {
  const options = useMemo(() => scopePickerOptions(rows, selection), [rows, selection]);
  const update = (next: ScopeSelection) => onChange(pruneScopeSelection(rows, next));

  return <div className="flex flex-wrap items-end gap-3">
    <ScopeMulti label="Product"><MultiCombobox ariaLabel="Product" value={selection.tracking} onChange={(tracking) => update({ ...selection, tracking })} options={options.products} placeholder="Search products" emptyText="No matching product." disabled={disabled} nonMutating /></ScopeMulti>
    <ScopeMulti label="Variant"><MultiCombobox ariaLabel="Variant" value={selection.variant} onChange={(variant) => update({ ...selection, variant })} options={options.variants} placeholder="Search variants" emptyText="No matching variant." disabled={disabled} nonMutating /></ScopeMulti>
    <FilterBar
      filters={[choice("region", "Region", REGIONS)]}
      values={{ region: selection.region }}
      layout="inline"
      onChange={(next) => update({ ...selection, region: text(next.region) })}
    />
    <ScopeMulti label="Channel"><MultiCombobox ariaLabel="Channel" value={selection.channel} onChange={(channel) => update({ ...selection, channel })} options={options.channels} placeholder="Search channels" emptyText="No matching channel." disabled={disabled} nonMutating /></ScopeMulti>
    <ScopeMulti label="SKU" wide><MultiCombobox ariaLabel="SKU" value={selection.sku} onChange={(sku) => onChange(selectScopeSkus(selection, sku))} options={options.skus} placeholder="Search SKUs" emptyText="No matching SKU." disabled={disabled} nonMutating /></ScopeMulti>
  </div>;
}

function ScopeMulti({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return <div className={`grid gap-1.5 ${wide ? "w-64" : "w-48"} max-w-full`}>
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
    {children}
  </div>;
}
